import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Coffee, Pause } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { FocusPhase } from '@/lib/focus/focusMachine';

export interface FocusPillProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  phase: Exclude<FocusPhase, 'idle'>;
  timeLabel: string;
  intention?: string;
  /** Minutes left, for the accessible name - "18:42" read aloud is noise. */
  minutesLeft?: number | null;
}

const TONE = {
  focus:
    'border-transparent bg-primary text-primary-foreground dark:bg-tint-brand-strong dark:text-tint-brand-strong-foreground',
  // Full-strength ink: muted text on the secondary fill is under AA.
  paused: 'border-border bg-secondary text-foreground',
  break: 'border-transparent bg-tint-brand text-tint-brand-foreground',
} as const;

function accessibleName(
  phase: FocusPillProps['phase'],
  minutesLeft: number | null | undefined
): string {
  const label = {
    focus: 'Focus timer',
    paused: 'Focus timer, paused',
    break: 'Break timer',
  }[phase];
  if (minutesLeft === null || minutesLeft === undefined) return label;
  const unit = minutesLeft === 1 ? 'minute' : 'minutes';
  return `${label}, ${minutesLeft} ${unit} left`;
}

/**
 * The running timer in the Navbar: `● 18:42 · Photosynthesis`. Only shown
 * while a block or break is running; idle, the Wellbeing nav item is the way
 * in. Opens `FocusPanel`.
 */
export const FocusPill = forwardRef<HTMLButtonElement, FocusPillProps>(
  ({ phase, timeLabel, intention, minutesLeft, className, ...rest }, ref) => (
    <button
      ref={ref}
      type='button'
      aria-label={accessibleName(phase, minutesLeft)}
      className={cn(
        'tap-target inline-flex h-9 items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        TONE[phase],
        className
      )}
      {...rest}
    >
      {phase === 'focus' && (
        <span className='relative flex h-2 w-2' aria-hidden='true'>
          <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-40' />
          <span className='relative inline-flex h-2 w-2 rounded-full bg-current' />
        </span>
      )}
      {phase === 'paused' && (
        <Pause className='h-3.5 w-3.5' aria-hidden='true' />
      )}
      {phase === 'break' && (
        <Coffee className='h-3.5 w-3.5' aria-hidden='true' />
      )}
      <span className='font-mono tabular-nums' aria-hidden='true'>
        {timeLabel}
      </span>
      {phase === 'break' ? (
        <span aria-hidden='true'>Break</span>
      ) : (
        intention && (
          <span
            className='hidden max-w-[10rem] truncate font-normal lg:inline'
            aria-hidden='true'
          >
            · {intention}
          </span>
        )
      )}
    </button>
  )
);
FocusPill.displayName = 'FocusPill';
